/**
 * ahooks cacheKey registry — exact-key invalidation for pattern clears.
 *
 * ahooks' runtime `clearCache` only accepts an exact key (or key array); the
 * predicate/callback form (`clearCache((key) => …)`) used across the mutation
 * hooks was a SILENT NO-OP — it resolves to `Map.delete(fn)`, deleting nothing,
 * so list/detail caches stayed stale until `staleTime` expired. See
 * `node_modules/ahooks/lib/useRequest/src/utils/cache.js`.
 *
 * ahooks exposes no way to enumerate its internal cache Map, so we record every
 * issued cacheKey here (via `trackCacheKey`, wrapping the read hooks' `cacheKey`
 * assignments). Pattern-based invalidation (`clearCacheWhere`) then resolves the
 * predicate against the tracked keys and clears the exact keys ahooks understands.
 *
 * `useTaxonomy.ts` avoids the registry entirely by using static `/api/taxonomy/*`
 * cacheKeys — prefer static keys when a resource has no param-serialized variants.
 */

import { clearCache } from 'ahooks';

/**
 * Every cacheKey ever handed to ahooks through {@link trackCacheKey}. Never
 * pruned: re-clearing an already-evicted key is a harmless Map.delete no-op, and
 * live hooks re-register their key on the next render, so accumulation is bounded
 * by the (small) set of distinct param combinations a session actually issues.
 */
const issuedKeys = new Set<string>();

/**
 * Record an issued ahooks cacheKey and return it unchanged, so it can wrap a
 * `cacheKey:` assignment inline:
 *
 * ```ts
 * cacheKey: trackCacheKey(vendorId ? `/api/vendors/${vendorId}/banks` : undefined)
 * ```
 *
 * `undefined` (not-ready hooks) passes straight through untracked.
 */
export function trackCacheKey<T extends string | undefined>(key: T): T {
  if (typeof key === 'string') {
    issuedKeys.add(key);
  }
  return key;
}

/**
 * Exact-key replacement for the broken `clearCache((key) => …)` predicate form.
 * Resolves `predicate` against every tracked key and clears the matches in a
 * single `clearCache(string[])` call.
 */
export function clearCacheWhere(predicate: (key: string) => boolean): void {
  const matches: string[] = [];
  issuedKeys.forEach((key) => {
    if (predicate(key)) {
      matches.push(key);
    }
  });
  if (matches.length > 0) {
    clearCache(matches);
  }
}

/** Snapshot of the currently tracked keys (test/debug only). */
export function __getTrackedCacheKeys(): string[] {
  return Array.from(issuedKeys);
}
